import React, { Component } from 'react';
import { connect } from 'react-redux';
import { reduxForm, formValueSelector } from 'redux-form';
import { Icon, Form } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import ReactTable from 'Common/ReactTableWrapper';
import {
  caseInsensitiveFilter,
  numericSortMethod,
  qtyOnHandSortMethod,
  dateSortMethod,
} from 'Common/GridHelper';
import { GridEnum } from 'Common/Constants/Enumeration/GridEnum';
import { ItemEnum, AdjustmentEnum } from 'Common/FieldEnum';
import { CREATE_EDIT_ADJUSTMENT_FORM_NAME } from 'Common/FormEnum';
import { TableNamesEnum } from 'Common/Constants/Enumeration/TableEnum';
import EditableLabel from 'Components/editableLabel/EditableLabel';
import { ReactTableWrapperColumnProperties } from 'Common/Constants';
import {  TabDisabledInput } from 'Common/FormHelper';
import { ToolTipComponent } from 'Components';
import { PACK_SIZE_MESSAGE } from 'Common/Constants/inventory';
import * as GridPreferenceSelectors from 'State/gridPreference/selectors';
import {BracketTypeEnum} from "Common/Constants/Enumeration/ManageProcessOrderEnum";

import { dollarCalculationAdj, quantityCalculationAdj, weightCalculationAdj } from './../../helper';

class AdjustmentInfoContainer extends Component {
  static propTypes = {
    dataSource: PropTypes.array.isRequired,
    fetchingAdjustmentItems: PropTypes.bool,
    fetchingAdjustmentDetail: PropTypes.bool,
    onResetPageOnDataChange: PropTypes.func,
    resetPageOnDataChange: PropTypes.bool,
    adjustmentItemsInfo: PropTypes.array,
    gridPreferenceLoading: PropTypes.bool,
    gridPreferenceSubmitting: PropTypes.bool,
  };
  static defaultProps = {
    onResetPageOnDataChange: () => {},
    adjustmentItemsInfo: [],
    gridPreferenceLoading: false,
    gridPreferenceSubmitting: false,
  };
  constructor(props) {
    super(props);
    const columns = [
      {
        Header: 'Item Code',
        accessor: ItemEnum.ItemCode,
        ...ReactTableWrapperColumnProperties,
      },
      {
        Header: 'Description',
        accessor: ItemEnum.Description,
        ...ReactTableWrapperColumnProperties,
        width: 200,
      },
      {
        Header: () => {
          return (
            <ToolTipComponent
              label={'Pack Size'}
              popMessage={PACK_SIZE_MESSAGE}
              iconName="question circle outline"
            />
          );
        },
        visibilityName: 'Pack Size',
        accessor: ItemEnum.QtyPerUnit,
        ...ReactTableWrapperColumnProperties,
      },
      {
        Header: 'Item Packaging',
        accessor: ItemEnum.PkgDescription,
        ...ReactTableWrapperColumnProperties,
      },
      {
        Header: 'Kitchen Item',
        accessor: ItemEnum.PrepareInHouse,
        ...ReactTableWrapperColumnProperties,
      },
      {
        Header: 'Price',
        accessor: ItemEnum.Price,
        Filter: TabDisabledInput,
        sortMethod: numericSortMethod,
        ...ReactTableWrapperColumnProperties,
        Cell: ({ original }) => {
          let price = original[ItemEnum.Price];
          if(original.BracketPricingDetails && original.BracketPricingDetails.length > 0){
            if (original.BracketType === BracketTypeEnum.Dollars) {
              price = dollarCalculationAdj(
                  original,
                  +original.BracketPricingDetails[0].Price,
                  true
              );
            } else if (original.BracketType === BracketTypeEnum.Quantity) {
              price = quantityCalculationAdj(original, true);
            } else if (original.BracketType === BracketTypeEnum.Weight) {
              price = weightCalculationAdj(original, true);
            }
          }
          return <span>{Number(price).toFixed(4)}</span>
        },
      },
      {
        Header: () => {
          return (
            <span>
              Purchase
              <br />
              Qty
            </span>
          );
        },
        visibilityName: 'Purchase Qty',
        accessor: ItemEnum.PriQtyAdj,
        className: 'right-align',
        ...ReactTableWrapperColumnProperties,
        visibility: false,
        Cell: ({ original }) => {
          return !props.disabled ? (
            <EditableLabel
              label={
                original[ItemEnum.PriQtyAdj] ? original[ItemEnum.PriQtyAdj] : ''
              }
              labelClassName={'qtyLabel'}
              refData={original}
              inputType={'text'}
              onChange={this.handleChangePriQty}
              disabled={props.disabled}
              allowEdit={!props.disabled}
            />
          ) : (
            <span>{original.PriQtyAdj}</span>
          );
        },
        Filter: TabDisabledInput,
      },
      {
        Header: 'Unit',
        accessor: ItemEnum.Unit,
        ...ReactTableWrapperColumnProperties,
      },
      {
        Header: () => {
          return (
            <span>
              Pack <br />
              Qty
            </span>
          );
        },
        visibilityName: 'Pack Qty',
        accessor: ItemEnum.SecQtyAdj,
        className: 'right-align',
        ...ReactTableWrapperColumnProperties,
        visibility: false,
        Cell: ({ original }) => {
          return !props.disabled ? (
            <EditableLabel
              label={
                original[ItemEnum.SecQtyAdj] ? original[ItemEnum.SecQtyAdj] : ''
              }
              labelClassName={'qtyLabel'}
              refData={original}
              inputType={'text'}
              onChange={this.handleChangeSecQty}
              disabled={props.disabled}
              allowEdit={!props.disabled}
              showCalc={true}
            />
          ) : (
            <span>{original.SecQtyAdj}</span>
          );
        },
        Filter: TabDisabledInput,
      },
      {
        Header: 'Pack Unit',
        accessor: ItemEnum.SecondaryUnit,
        ...ReactTableWrapperColumnProperties,
      },
      {
        Header: 'Qty On Hand',
        accessor: ItemEnum.QtyOnHand_Detail,
        show: !props.disabled,
        sortMethod: qtyOnHandSortMethod,
        ...ReactTableWrapperColumnProperties,
      },
      {
        Header: 'Alternate Item',
        accessor: ItemEnum.AlternateItemId,
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        Header: 'Account Category',
        accessor: ItemEnum.AccountCategory,
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        Header: 'Distributor',
        accessor: ItemEnum.VendorName,
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        Header: 'Bid Group',
        accessor: ItemEnum.BidGroup,
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        Header: 'Tax Group',
        accessor: ItemEnum.TaxGroup,
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        Header: 'Distributor Item',
        accessor: ItemEnum.VendorItem,
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        Header: 'Brand',
        accessor: ItemEnum.CurrentBrand,
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        Header: 'UPC',
        accessor: ItemEnum.UPC,
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        Header: 'GTIN',
        accessor: ItemEnum.GTINNumber,
        className: 'right-align',
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        Header: 'Begin Date',
        accessor: ItemEnum.BeginDate,
        sortMethod: dateSortMethod,
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        Header: 'End Date',
        accessor: ItemEnum.EndDate,
        sortMethod: dateSortMethod,
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        Header: 'Servings Per Purchase Unit',
        accessor: ItemEnum.ServingsPerPrimaryUnit,
        className: 'right-align',
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        Header: 'Menu Costing',
        accessor: ItemEnum.MenuCosting,
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        Header: 'Order Preference',
        accessor: ItemEnum.OrderPreference,
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        Header: 'Storage Location',
        accessor: ItemEnum.StorageLocation,
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        Header: 'Minimum Par Level',
        accessor: ItemEnum.MinimumParLevel,
        className: 'right-align',
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        Header: 'Maximum Par Level',
        accessor: ItemEnum.MaximumParLevel,
        className: 'right-align',
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        Header: 'Truck Route',
        accessor: ItemEnum.TruckRoute,
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        resizable: false,
        width: 50,
        sortable: false,
        filterable: false,
        Cell: ({ original }) => {
          return (
            (original[ItemEnum.SecQtyAdj] ||
              original[ItemEnum.SecQtyAdj] === 0 ||
              original[ItemEnum.PriQtyAdj] ||
              original[ItemEnum.PriQtyAdj] === 0) &&
            !this.props.disabled && (
              <Icon
                disabled={props.disabled}
                name={'minus circle'}
                style={{
                  cursor: 'pointer',
                }}
                onClick={() => {
                  this.handleRemoveItem(original);
                }}
              />
            )
          );
        },
      },
    ];
    if (props.disabled) {
      columns.splice(9, 0, {
        Header: 'Net Value Change',
        accessor: ItemEnum.NetValueChange,
        className: 'right-align',
        ...ReactTableWrapperColumnProperties,
      });
    }
    this.state = {
      selected: [],
      isPreferenceDataChange: false,
      columns: columns,
    };
  }

  handleChangePriQty = (refData, newValue, defaultValue) => {
    this.handleChangeQty(refData, newValue, ItemEnum.PriQtyAdj, defaultValue);
  };

  handleChangeSecQty = (refData, newValue, defaultValue) => {
    this.handleChangeQty(refData, newValue, ItemEnum.SecQtyAdj, defaultValue);
  };

  handleChangeQty = (refData, newValue, Qty, defaultValue) => {
    let value = newValue.match(/^-?\d{0,6}(\.\d{0,4})?/)[0];
    if (!value && newValue) {
      value = defaultValue;
    }

    const formValues =
      this.props.input.value && this.props.input.value.length
        ? _.cloneDeep(this.props.input.value)
        : [];
    const index = _.findIndex(formValues, formValue => {
      return (
        formValue.ItemCode === refData.ItemCode &&
        formValue.Price === refData.Price
      );
    });
    const primOrSecQty =
      Qty === ItemEnum.PriQtyAdj ? ItemEnum.SecQtyAdj : ItemEnum.PriQtyAdj;
    if (index > -1) {
      if (value && value.trim().length) {
        formValues[index][Qty] = value;
      } else if (!refData[primOrSecQty] || !refData[primOrSecQty].toString()) {
        if (this.props.isPreviewPage) {
          formValues[index][Qty] = value;
        } else {
          _.remove(formValues, field => {
            return (
              field.ItemCode === refData.ItemCode &&
              field.Price === refData.Price
            );
          });
        }
      } else {
        formValues[index][Qty] = value;
      }
    } else {
      const newFiled = Object.assign({}, refData);
      newFiled[Qty] = value;
      if (value.trim().length) {
        formValues.push(newFiled);
      }
    }
    this.props.input.onChange(formValues);
  };

  handleRemoveItem = original => {
    const currentFields = _.cloneDeep(this.props.input.value);
    _.remove(currentFields, item => {
      return (
        original.ItemCode === item.ItemCode && original.Price === item.Price
      );
    });
    this.props.input.onChange(currentFields);
  };

  prepareTableData = () => {
    const dataSourceCopy =
      this.props.dataSource && this.props.dataSource.length
        ? _.cloneDeep(this.props.dataSource)
        : [];
    const { value } = this.props.input;
    if (value && value.length && dataSourceCopy && dataSourceCopy.length) {
      _.each(value, item => {
        const index = _.findIndex(dataSourceCopy, {
          [ItemEnum.ItemCode]: item[ItemEnum.ItemCode],
          [ItemEnum.Price]: item[ItemEnum.Price],
        });
        if (index > -1) {
          const dataRow = { ...item };
          dataSourceCopy[index][ItemEnum.PriQtyAdj] =
            dataRow[ItemEnum.PriQtyAdj];
          dataSourceCopy[index][ItemEnum.SecQtyAdj] =
            dataRow[ItemEnum.SecQtyAdj];
        }
      });
    }
    return dataSourceCopy;
  };

  getTrProps = (state, rowInfo) => {
    let selected = false;
    if (
      rowInfo &&
      rowInfo.original &&
      (rowInfo.original[ItemEnum.PriQtyAdj] ||
        rowInfo.original[ItemEnum.SecQtyAdj])
    ) {
      selected = true;
    }
    return {
      style: {
        color: selected && !this.props.disabled ? '#df5a17' : '',
        fontWeight: selected && !this.props.disabled ? '600' : '500',
      },
    };
  };

  render() {
    const {
      fetchingAdjustmentItems,
      gridPreferenceLoading,
      gridPreferenceSubmitting,
      fetchingAdjustmentDetail,
      resetPageOnDataChange,
      onResetPageOnDataChange,
    } = this.props;
    return (
      <Form.Field>
        <ReactTable
          minRows={0}
          loading={
            fetchingAdjustmentItems ||
            gridPreferenceLoading ||
            gridPreferenceSubmitting ||
            fetchingAdjustmentDetail
          }
          data={this.prepareTableData()}
          reactTableRef={this.reactTable}
          resizable={true}
          defaultFilterMethod={caseInsensitiveFilter}
          noDataText={fetchingAdjustmentItems ? '' : 'No items found'}
          draggableColumns={true}
          showHideColumn={true}
          menu={'hasCogs'}
          saveState={
            this.props.disabled
              ? {
                  tableName: TableNamesEnum.ADJUSTMENT_LIST_VIEW_TABLE,
                }
              : {
                  tableName: TableNamesEnum.ADJUSTMENT_LIST_CREATE_EDIT_TABLE,
                }
          }
          getTrProps={this.getTrProps}
          columns={this.state.columns}
          resetPageOnDataChange={resetPageOnDataChange}
          onResetPageOnDataChange={onResetPageOnDataChange}
          gridName={
            this.props.disabled
              ? GridEnum.INV_AdjustmentList_Edit_View
              : GridEnum.INV_AdjustmentList_CreateEdit_View
          }
        />
      </Form.Field>
    );
  }
}

const selector = formValueSelector(CREATE_EDIT_ADJUSTMENT_FORM_NAME);
const mapStateToProps = state => {
  return {
    adjustmentItemsInfo: selector(state, [AdjustmentEnum.AdjustmentItemsInfo]),
    gridPreferenceLoading: GridPreferenceSelectors.getLoading(state),
    gridPreferenceSubmitting: GridPreferenceSelectors.getIsSubmitting(state),
  };
};
AdjustmentInfoContainer = reduxForm({
  form: CREATE_EDIT_ADJUSTMENT_FORM_NAME,
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
})(AdjustmentInfoContainer);

export const AdjustmentInfo = connect(
  mapStateToProps,
  null
)(AdjustmentInfoContainer);



// WEBPACK FOOTER //
// ./src/pages/inventory/transaction/adjustment/containers/createEditAdjustment/AdjustmentInfo.js